import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Camera,
  ClipboardCheck,
  Send,
  Loader2,
  Sparkles,
  ShowerHead,
  Home,
  TreePine,
  Trees,
  CheckCircle2,
  Key,
  Video,
} from "lucide-react";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Cleaner-facing job sheet, sourced from the two operations spreadsheets
 * (CLEANING SCHEDULE.xlsx + CKS TEAM SUPERVISOR.xlsx).
 *
 * Items marked `photo: true` are the high-scrutiny rows from the source
 * sheet that explicitly require photographic evidence at handover.
 */
const SECTIONS = [
  {
    id: "prep",
    title: "Pre-Clean Checks (Before You Start)",
    icon: Key,
    items: [
      { id: "p1", label: "Check for keys, windows, door locks, alarm codes and lights" },
      { id: "p2", label: "Collect cleaning items and products, referral card — kept in a visible spot" },
    ],
  },
  {
    id: "hallways",
    title: "Main Entrance, Hallways, Stairs & Entrance",
    icon: Trees,
    items: [
      { id: "h1", label: "Front door cleaned inside and out, letterbox and handle wiped" },
      { id: "h2", label: "Wipe skirting boards" },
      { id: "h3", label: "Stairs vacuumed / washed including edges" },
      { id: "h4", label: "Handrails wiped down" },
      { id: "h5", label: "Loft hatch wiped clean and checked for left-behind items" },
      { id: "h6", label: "Meter cupboard / cubby cleaned, meter covers dust-free" },
      { id: "h7", label: "Light switches and doorbell wiped" },
    ],
  },
  {
    id: "kitchen",
    title: "Kitchen — Highest Scrutiny Area",
    icon: Sparkles,
    items: [
      { id: "k1",  label: "Oven interior, racks, grill pan and door glass (inside and out) degreased", note: "Carbon build-up is the #1 deduction trigger — use oven-specific degreaser.", photo: true },
      { id: "k2",  label: "Hob, extractor fan and filter cleaned or filter replaced", note: "Grease film on extractor is checked every time.", photo: true },
      { id: "k3",  label: "Fridge / freezer defrosted, cleaned inside and out, door seals wiped", note: "Check for odours. Leave doors slightly open after if vacant.", photo: true },
      { id: "k4",  label: "Microwave cleaned inside and out" },
      { id: "k5",  label: "Washing machine / dishwasher drum, seal and detergent drawer cleaned", note: "Mould in the rubber door seal is a common flagged item.", photo: true },
      { id: "k6",  label: "Cupboards and drawers wiped inside, outside and on top", note: "Tops of cupboards collect grease and dust — always checked." },
      { id: "k7",  label: "Worktops and splashbacks degreased, including the gap between worktop and wall" },
      { id: "k8",  label: "Sink, taps and drain trap descaled and polished, food debris cleared" },
      { id: "k9",  label: "Behind and underneath all appliances cleaned (fridge, cooker, washer)", note: "Pull out — inspectors do.", photo: true },
      { id: "k10", label: "Tiles and grout cleaned" },
      { id: "k11", label: "Bin area cleaned and odour-free" },
      { id: "k12", label: "Flooring washed, including edges and under kickboards" },
      { id: "k13", label: "Light fittings and switches dust-free" },
    ],
  },
  {
    id: "bathroom",
    title: "Bathroom(s)",
    icon: ShowerHead,
    items: [
      { id: "b1",  label: "Bath / shower screen descaled, all limescale removed" },
      { id: "b2",  label: "Silicone sealant around bath / shower cleaned — no black mould", note: "Common deduction trigger. Replace sealant if mould won't lift.", photo: true },
      { id: "b3",  label: "Grout lines scrubbed, no black mould in grout" },
      { id: "b4",  label: "Toilet cleaned inside, outside, behind and at the base, including waterline" },
      { id: "b5",  label: "Sink, taps and plughole descaled and polished" },
      { id: "b6",  label: "Mirror and glass streak-free" },
      { id: "b7",  label: "Extractor fan cover removed and blades cleaned" },
      { id: "b8",  label: "Tiles wiped down fully, no soap scum" },
      { id: "b9",  label: "Flooring washed including behind toilet and under sink pedestal" },
      { id: "b10", label: "Towel rails, fittings and toilet roll holder clean" },
    ],
  },
  {
    id: "living",
    title: "Living Areas & Bedrooms",
    icon: Home,
    items: [
      { id: "l1",  label: "Carpets vacuumed thoroughly, including under furniture footprint marks", note: "Professional carpet clean required if stained." },
      { id: "l2",  label: "Hard flooring washed and polished where applicable" },
      { id: "l3",  label: "Skirting boards wiped down full length" },
      { id: "l4",  label: "Walls spot-cleaned, marks removed where possible (not full repaint unless damage beyond wear and tear)" },
      { id: "l5",  label: "Windows cleaned inside, including sills and tracks / runners", note: "Dust and debris in tracks is always inspected.", photo: true },
      { id: "l6",  label: "Curtains / blinds dusted or laundered if soiled" },
      { id: "l7",  label: "Light fittings, switches and sockets dust-free" },
      { id: "l8",  label: "Wardrobes and built-in storage cleaned inside and out" },
      { id: "l9",  label: "Radiators cleaned front and behind" },
      { id: "l10", label: "Cobwebs removed from ceilings and corners" },
    ],
  },
  {
    id: "outdoor",
    title: "Outdoor Areas (where tenant-responsibility)",
    icon: TreePine,
    items: [
      { id: "o1", label: "Front pathway and entrance swept" },
      { id: "o2", label: "Bins emptied and stored correctly" },
      { id: "o3", label: "Garden / patio / balcony cleared of debris" },
      { id: "o4", label: "External windows wiped (ground floor only)" },
      { id: "o5", label: "Shed / outbuilding tidy (where included)" },
    ],
  },
];

const allItemIds = SECTIONS.flatMap((s) => s.items.map((i) => i.id));

const DRAFT_KEY = "ck_job_sheet_draft_id";
const AUTOSAVE_MS = 15000;

const readOrCreateDraftId = () => {
  if (typeof window === "undefined") return null;
  try {
    const existing = window.localStorage.getItem(DRAFT_KEY);
    if (existing) return existing;
    const fresh = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage.setItem(DRAFT_KEY, fresh);
    return fresh;
  } catch {
    return null;
  }
};

export const JobSheetTab = ({ accessCode, cleanerName }) => {
  const [draftId, setDraftId] = useState(() => readOrCreateDraftId());
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [hydrating, setHydrating] = useState(true);
  const [job, setJob] = useState({
    property_address: "",
    booking_ref: "",
    cleaner_name: cleanerName || "",
    visit_date: new Date().toISOString().slice(0, 10),
  });

  // Sync the cleanerName prop into state whenever it resolves later. React
  // only runs the useState initializer on first mount, but `me` (which
  // feeds cleanerName) is fetched in parallel with authentication so it
  // often arrives after this component has already rendered with "".
  useEffect(() => {
    if (cleanerName) {
      setJob((prev) => (prev.cleaner_name ? prev : { ...prev, cleaner_name: cleanerName }));
    }
  }, [cleanerName]);
  const [checked, setChecked] = useState({}); // { itemId: true }
  const [signoff, setSignoff] = useState({
    supervisor_name: "",
    notes: "",
    customer_present: false,
    photos_taken: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Hydrate any existing server draft on mount so a cleaner can pick up
  // exactly where they left off even after the tab closed.
  useEffect(() => {
    if (!draftId) {
      setHydrating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = accessCode ? { access_code: accessCode } : {};
        const { data } = await axios.get(
          `${API}/cleaner/job-sheets/draft/${draftId}`,
          { params }
        );
        if (cancelled || !data?.payload) return;
        const p = data.payload;
        if (p.job) setJob((prev) => ({ ...prev, ...p.job }));
        if (p.checked) setChecked(p.checked);
        if (p.signoff) setSignoff((prev) => ({ ...prev, ...p.signoff }));
        if (data.updated_at) setLastSavedAt(data.updated_at);
      } catch {
        // 404 is normal (fresh device) — nothing to hydrate.
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Autosave every 15s while there is something worth saving.
  const savingRef = useRef(false);
  useEffect(() => {
    if (submitted || hydrating || !draftId) return undefined;
    const hasContent =
      job.property_address ||
      job.cleaner_name ||
      Object.values(checked).some(Boolean) ||
      signoff.notes;
    if (!hasContent) return undefined;

    const timer = setInterval(async () => {
      if (savingRef.current) return;
      savingRef.current = true;
      try {
        const { data } = await axios.post(`${API}/cleaner/job-sheets/draft`, {
          access_code: accessCode || "",
          draft_id: draftId,
          payload: { job, checked, signoff },
        });
        if (data?.updated_at) setLastSavedAt(data.updated_at);
      } catch (err) {
        logError("job sheet autosave failed", err);
      } finally {
        savingRef.current = false;
      }
    }, AUTOSAVE_MS);
    return () => clearInterval(timer);
  }, [job, checked, signoff, draftId, accessCode, submitted, hydrating]);

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const total = allItemIds.length;
  const progress = total ? Math.round((checkedCount / total) * 100) : 0;

  const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));

  const handleSubmit = async () => {
    if (!job.property_address || !job.cleaner_name) {
      toast.error("Property address and cleaner name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const completed_items = Object.keys(checked).filter((k) => checked[k]);
      await axios.post(`${API}/cleaner/job-sheets`, {
        access_code: accessCode || "",
        ...job,
        ...signoff,
        completed_items,
        total_items: total,
      });
      toast.success("Log Schedule submitted — logged with the team.");
      setSubmitted(true);
      // Clear the draft from server + browser once we've persisted the real thing.
      if (draftId) {
        try {
          const params = accessCode ? { access_code: accessCode } : {};
          await axios.delete(`${API}/cleaner/job-sheets/draft/${draftId}`, { params });
        } catch {
          // Non-fatal — the draft will just be overwritten next time.
        }
        try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        setDraftId(null);
      }
    } catch (err) {
      logError("job sheet submit failed", err);
      toast.error(err?.response?.data?.detail || "Could not submit log schedule.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-white border-[#E2E8E0]" data-testid="job-sheet-success">
        <CardContent className="text-center py-12">
          <CheckCircle2 className="w-14 h-14 text-[#7CB342] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#1A2E12] mb-2">Log Schedule submitted</h3>
          <p className="text-sm text-[#5C6B57] mb-6">
            {checkedCount} of {total} items completed on this visit. The office has the record.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setChecked({});
              setSignoff({ supervisor_name: "", notes: "", customer_present: false, photos_taken: false });
              setJob((p) => ({ ...p, property_address: "", booking_ref: "" }));
            }}
            className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
            data-testid="new-job-sheet"
          >
            Start a new log
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="job-sheet-tab">
      {/* Job header */}
      <Card className="bg-white border-[#E2E8E0]">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-[#2D5016]" />
            <div className="flex-1">
              <h3 className="font-semibold text-[#1A2E12]">CKS Log Schedule</h3>
              <p className="text-xs text-[#5C6B57]">
                Before you start — prepare to take photos of the rows marked <Camera className="inline w-3 h-3 -mt-0.5" /> below.
              </p>
            </div>
            <div className="text-right shrink-0" data-testid="autosave-indicator">
              {lastSavedAt ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-[#2D5016] bg-[#E8F5E9] px-2.5 py-1 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  Saved {new Date(lastSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              ) : (
                <span className="text-[11px] text-[#5C6B57]">Autosaving…</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6B57]">Property address *</Label>
              <Input
                value={job.property_address}
                onChange={(e) => setJob((p) => ({ ...p, property_address: e.target.value }))}
                placeholder="e.g. 12 Inkerman Rd, NW5 3BT"
                className="mt-1"
                data-testid="job-property-address"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B57]">Booking reference</Label>
              <Input
                value={job.booking_ref}
                onChange={(e) => setJob((p) => ({ ...p, booking_ref: e.target.value }))}
                placeholder="CK-BKG-XXXX (optional)"
                className="mt-1"
                data-testid="job-booking-ref"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B57]">Cleaner name *</Label>
              <Input
                value={job.cleaner_name}
                onChange={(e) => setJob((p) => ({ ...p, cleaner_name: e.target.value }))}
                className="mt-1"
                data-testid="job-cleaner-name"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B57]">Visit date</Label>
              <Input
                type="date"
                value={job.visit_date}
                onChange={(e) => setJob((p) => ({ ...p, visit_date: e.target.value }))}
                className="mt-1"
                data-testid="job-visit-date"
              />
            </div>
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between text-xs text-[#5C6B57] mb-1">
              <span>Progress</span>
              <span data-testid="job-progress">{checkedCount} / {total} ({progress}%)</span>
            </div>
            <div className="h-2 bg-[#E2E8E0] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#7CB342] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Accordion type="multiple" defaultValue={["prep","hallways"]} className="space-y-3">
        {SECTIONS.map((section) => {
          const sectionDone = section.items.every((i) => checked[i.id]);
          return (
            <AccordionItem
              key={section.id}
              value={section.id}
              className="bg-white border border-[#E2E8E0] rounded-xl px-4"
              data-testid={`section-${section.id}`}
            >
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3">
                  <section.icon className="w-5 h-5 text-[#2D5016]" />
                  <span className="font-semibold text-[#1A2E12] text-left">{section.title}</span>
                  {sectionDone && (
                    <CheckCircle2 className="w-4 h-4 text-[#7CB342]" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-3 pb-2">
                  {section.items.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-start gap-3"
                      data-testid={`item-${item.id}`}
                    >
                      <Checkbox
                        id={item.id}
                        checked={!!checked[item.id]}
                        onCheckedChange={() => toggle(item.id)}
                        className="mt-0.5"
                        data-testid={`item-check-${item.id}`}
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={item.id}
                          className={`text-sm cursor-pointer ${
                            checked[item.id]
                              ? "line-through text-[#5C6B57]"
                              : "text-[#1A2E12]"
                          }`}
                        >
                          {item.label}
                          {item.photo && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs bg-[#FFF4D6] text-[#7C5A00] px-2 py-0.5 rounded-full">
                              <Camera className="w-3 h-3" />
                              Photo
                            </span>
                          )}
                        </label>
                        {item.note && (
                          <p className="text-xs text-[#5C6B57] mt-1 italic">{item.note}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Final video reminder */}
      <Card className="bg-[#FFF4D6] border-[#F0D77A]" data-testid="video-reminder">
        <CardContent className="p-5 flex items-start gap-3">
          <Video className="w-6 h-6 text-[#7C5A00] mt-1" />
          <div>
            <h3 className="font-semibold text-[#1A2E12]">Final walkthrough — record a video</h3>
            <p className="text-sm text-[#5C6B57] mt-1">
              Before you tick <em>Photos taken</em> below, take one <strong>continuous video of each room</strong>. Include every row marked <span className="inline-flex items-center gap-1 bg-white px-2 rounded-full text-xs"><Camera className="w-3 h-3" /> Photo</span> above. Store the clips on your phone under the property address / booking reference and share to the office channel at end of shift.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Final sign-off */}
      <Card className="bg-[#F9FAF9] border-[#E2E8E0]" data-testid="signoff-section">
        <CardContent className="p-5 space-y-4">
          <h3 className="font-semibold text-[#1A2E12] flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-[#2D5016]" />
            Final sign-off
          </h3>
          <div>
            <Label className="text-xs text-[#5C6B57]">Supervisor name (if applicable)</Label>
            <Input
              value={signoff.supervisor_name}
              onChange={(e) => setSignoff((p) => ({ ...p, supervisor_name: e.target.value }))}
              className="mt-1"
              data-testid="signoff-supervisor-name"
            />
          </div>
          <div>
            <Label className="text-xs text-[#5C6B57]">Notes / issues to flag</Label>
            <Textarea
              rows={3}
              value={signoff.notes}
              onChange={(e) => setSignoff((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Anything the office should know — damage, access issues, customer requests..."
              className="mt-1"
              data-testid="signoff-notes"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-[#1A2E12] cursor-pointer">
              <Checkbox
                checked={signoff.customer_present}
                onCheckedChange={(v) => setSignoff((p) => ({ ...p, customer_present: !!v }))}
                data-testid="signoff-customer-present"
              />
              Customer present at handover
            </label>
            <label className="flex items-center gap-2 text-sm text-[#1A2E12] cursor-pointer">
              <Checkbox
                checked={signoff.photos_taken}
                onCheckedChange={(v) => setSignoff((p) => ({ ...p, photos_taken: !!v }))}
                data-testid="signoff-photos-taken"
              />
              Final walkthrough video recorded (all required rooms)
            </label>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
            data-testid="submit-job-sheet"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit log schedule
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default JobSheetTab;
